import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  TbPlus,
  TbEdit,
  TbTrash,
  TbPig,
  TbUsers,
  TbMoneybag,
  TbAlertCircle,
  TbCheck,
} from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import ModalLayout from '../../components/layouts/ModalLayout';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import { formatEuroCents } from '../../../utils/bankAccountUtil';

interface PotParticipant {
  id: number;
  name: string;
  balance: number; // in cents
  isActive: boolean;
}

interface Pot {
  id: number;
  name: string;
  description?: string;
  color: string;
  totalBalance: number; // in cents
  targetAmount?: number; // in cents, optional target
  status: 'active' | 'completed' | 'broken';
  participants: PotParticipant[];
  transactionCount: number;
  createdAt: string;
  completedAt?: string;
  brokenAt?: string;
}

interface PotsPageProps {
  hideHeader?: boolean;
}

export default function PotsPage({ hideHeader = false }: PotsPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.pots' });

  // Mock data for pots
  const [pots, setPots] = useState<Pot[]>([
    {
      id: 1,
      name: 'Wintersport Trip',
      description: 'Saving for our annual winter sports trip to Austria',
      color: '#3B82F6',
      totalBalance: 250000, // €2,500.00
      targetAmount: 500000, // €5,000.00
      status: 'active',
      participants: [
        { id: 1, name: 'John Doe', balance: 50000, isActive: true },
        { id: 2, name: 'Jane Smith', balance: 75000, isActive: true },
        { id: 3, name: 'Bob Johnson', balance: 60000, isActive: true },
        { id: 4, name: 'Alice Brown', balance: 65000, isActive: true },
      ],
      transactionCount: 12,
      createdAt: '2024-01-15',
    },
    {
      id: 2,
      name: 'Summer BBQ',
      description: 'Funds for our summer barbecue event',
      color: '#10B981',
      totalBalance: 45000, // €450.00
      targetAmount: 30000, // €300.00
      status: 'completed',
      participants: [
        { id: 5, name: 'Mike Wilson', balance: 15000, isActive: true },
        { id: 6, name: 'Sarah Davis', balance: 15000, isActive: true },
        { id: 7, name: 'Tom Miller', balance: 15000, isActive: true },
      ],
      transactionCount: 8,
      createdAt: '2024-02-01',
      completedAt: '2024-06-15',
    },
    {
      id: 3,
      name: 'Team Equipment',
      description: 'New equipment for the sports team',
      color: '#F59E0B',
      totalBalance: -15000, // -€150.00 (negative balance)
      targetAmount: 80000, // €800.00
      status: 'active',
      participants: [
        { id: 8, name: 'Lisa Garcia', balance: -5000, isActive: true },
        { id: 9, name: 'David Lee', balance: -5000, isActive: true },
        { id: 10, name: 'Emma Taylor', balance: -5000, isActive: true },
      ],
      transactionCount: 5,
      createdAt: '2024-03-10',
    },
    {
      id: 4,
      name: 'Conference Trip',
      description: 'Business conference attendance fund',
      color: '#8B5CF6',
      totalBalance: 0, // €0.00
      targetAmount: 120000, // €1,200.00
      status: 'broken',
      participants: [
        { id: 11, name: 'Alex Chen', balance: 0, isActive: false },
        { id: 12, name: 'Maria Rodriguez', balance: 0, isActive: false },
      ],
      transactionCount: 3,
      createdAt: '2024-01-20',
      brokenAt: '2024-04-05',
    },
  ]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBreakModalOpen, setIsBreakModalOpen] = useState(false);
  const [editingPot, setEditingPot] = useState<Pot | null>(null);
  const [breakingPot, setBreakingPot] = useState<Pot | null>(null);
  const [newPot, setNewPot] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    targetAmount: '',
  });

  const colorOptions = [
    { label: 'Blue', value: '#3B82F6' },
    { label: 'Green', value: '#10B981' },
    { label: 'Yellow', value: '#F59E0B' },
    { label: 'Red', value: '#EF4444' },
    { label: 'Purple', value: '#8B5CF6' },
    { label: 'Pink', value: '#EC4899' },
    { label: 'Indigo', value: '#6366F1' },
    { label: 'Teal', value: '#14B8A6' },
  ];

  const getStatusColor = (status: Pot['status']) => {
    switch (status) {
      case 'active':
        return 'text-success bg-green-100';
      case 'completed':
        return 'text-primary-300 bg-blue-100';
      case 'broken':
        return 'text-danger bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: Pot['status']) => {
    switch (status) {
      case 'active':
        return <TbPig className="h-4 w-4" />;
      case 'completed':
        return <TbCheck className="h-4 w-4" />;
      case 'broken':
        return <TbAlertCircle className="h-4 w-4" />;
      default:
        return <TbPig className="h-4 w-4" />;
    }
  };

  const handleCreatePot = () => {
    if (!newPot.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }

    const pot: Pot = {
      id: Math.max(...pots.map((p) => p.id)) + 1,
      name: newPot.name.trim(),
      description: newPot.description.trim() || undefined,
      color: newPot.color,
      totalBalance: 0,
      targetAmount: newPot.targetAmount ? parseInt(newPot.targetAmount, 10) * 100 : undefined,
      status: 'active',
      participants: [],
      transactionCount: 0,
      createdAt: new Date().toISOString().split('T')[0],
    };

    setPots((prev) => [...prev, pot]);
    setNewPot({ name: '', description: '', color: '#3B82F6', targetAmount: '' });
    setIsCreateModalOpen(false);
    toast.success(t('success.created'));
  };

  const handleEditPot = () => {
    if (!editingPot || !editingPot.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }

    setPots((prev) =>
      prev.map((pot) =>
        pot.id === editingPot.id
          ? {
              ...pot,
              name: editingPot.name.trim(),
              description: editingPot.description?.trim() || undefined,
              color: editingPot.color,
              targetAmount: editingPot.targetAmount,
            }
          : pot,
      ),
    );

    setEditingPot(null);
    setIsEditModalOpen(false);
    toast.success(t('success.updated'));
  };

  const handleDeletePot = (pot: Pot) => {
    if (pot.transactionCount > 0) {
      toast.error(t('errors.cannotDeleteWithTransactions'));
      return;
    }

    if (window.confirm(t('confirmations.delete', { name: pot.name }))) {
      setPots((prev) => prev.filter((p) => p.id !== pot.id));
      toast.success(t('success.deleted'));
    }
  };

  const handleBreakPot = () => {
    if (!breakingPot) return;

    if (window.confirm(t('confirmations.break', { name: breakingPot.name }))) {
      setPots((prev) =>
        prev.map((pot) =>
          pot.id === breakingPot.id
            ? {
                ...pot,
                status: 'broken' as const,
                brokenAt: new Date().toISOString().split('T')[0],
                participants: pot.participants.map((p) => ({ ...p, isActive: false })),
              }
            : pot,
        ),
      );

      setBreakingPot(null);
      setIsBreakModalOpen(false);
      toast.success(t('success.broken'));
    }
  };

  const openEditModal = (pot: Pot) => {
    setEditingPot({ ...pot });
    setIsEditModalOpen(true);
  };

  const openBreakModal = (pot: Pot) => {
    setBreakingPot(pot);
    setIsBreakModalOpen(true);
  };

  const activePots = pots.filter((pot) => pot.status === 'active');
  const completedPots = pots.filter((pot) => pot.status === 'completed');
  const brokenPots = pots.filter((pot) => pot.status === 'broken');

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <Button variant="primary-dark" onClick={() => setIsCreateModalOpen(true)}>
              <TbPlus />
              {t('addPot')}
            </Button>
          }
        />
      )}

      <div className="space-y-8">
        {/* Active Pots */}
        {activePots.length > 0 && (
          <div>
            <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('sections.active')}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {activePots.map((pot) => (
                <div
                  key={pot.id}
                  className="rounded-xl border border-secondary-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: pot.color }}>
                        <TbPig className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{pot.name}</h3>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(pot.status)}`}>
                            {getStatusIcon(pot.status)}
                            <span className="ml-1">{t(`status.${pot.status}`)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <ActionMenu>
                      <ActionMenuButton
                        icon={TbEdit}
                        text={t('edit')}
                        onClick={() => openEditModal(pot)}
                      />
                      <ActionMenuButton
                        icon={TbAlertCircle}
                        text={t('break')}
                        onClick={() => openBreakModal(pot)}
                        className="text-secondary hover:text-orange-400"
                      />
                      <ActionMenuButton
                        icon={TbTrash}
                        text={t('delete')}
                        onClick={() => handleDeletePot(pot)}
                        className="text-danger hover:text-red-700"
                      />
                    </ActionMenu>
                  </div>

                  {pot.description && (
                    <p className="mb-4 text-sm text-gray-600">{pot.description}</p>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('currentBalance')}</span>
                      <span
                        className={`font-semibold ${pot.totalBalance < 0 ? 'text-danger' : 'text-success'}`}>
                        {formatEuroCents(pot.totalBalance, i18n.language)}
                      </span>
                    </div>

                    {pot.targetAmount && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">{t('targetAmount')}</span>
                        <span className="font-semibold text-gray-900">
                          {formatEuroCents(pot.targetAmount, i18n.language)}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('participants')}</span>
                      <div className="flex items-center space-x-1">
                        <TbUsers className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium">
                          {pot.participants.filter((p) => p.isActive).length}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('transactions')}</span>
                      <div className="flex items-center space-x-1">
                        <TbMoneybag className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium">{pot.transactionCount}</span>
                      </div>
                    </div>

                    {pot.targetAmount && (
                      <div className="h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                          style={{
                            width: `${Math.min(100, Math.max(0, (pot.totalBalance / pot.targetAmount) * 100))}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Pots */}
        {completedPots.length > 0 && (
          <div>
            <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('sections.completed')}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {completedPots.map((pot) => (
                <div
                  key={pot.id}
                  className="rounded-xl border border-secondary-200 bg-white p-6 opacity-75 shadow-sm">
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: pot.color }}>
                        <TbCheck className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{pot.name}</h3>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(pot.status)}`}>
                          {getStatusIcon(pot.status)}
                          <span className="ml-1">{t(`status.${pot.status}`)}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('finalBalance')}</span>
                      <span className="font-semibold text-success">
                        {formatEuroCents(pot.totalBalance, i18n.language)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('completedOn')}</span>
                      <span className="text-sm text-gray-900">
                        {new Date(pot.completedAt!).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Broken Pots */}
        {brokenPots.length > 0 && (
          <div>
            <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('sections.broken')}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {brokenPots.map((pot) => (
                <div
                  key={pot.id}
                  className="rounded-xl border border-secondary-200 bg-white p-6 opacity-75 shadow-sm">
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: pot.color }}>
                        <TbAlertCircle className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{pot.name}</h3>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(pot.status)}`}>
                          {getStatusIcon(pot.status)}
                          <span className="ml-1">{t(`status.${pot.status}`)}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('finalBalance')}</span>
                      <span className="font-semibold text-gray-600">
                        {formatEuroCents(pot.totalBalance, i18n.language)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('brokenOn')}</span>
                      <span className="text-sm text-gray-900">
                        {new Date(pot.brokenAt!).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {pots.length === 0 && (
          <div className="py-12 text-center">
            <TbPig className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-medium text-gray-900">{t('emptyState.title')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('emptyState.description')}</p>
            <div className="mt-4">
              <Button variant="primary-dark" onClick={() => setIsCreateModalOpen(true)}>
                <TbPlus />
                {t('addFirstPot')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Pot Modal */}
      <ModalLayout
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[20px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('createModal.title')}</h2>
          <div className="space-y-4">
            <Input
              id="pot-name"
              label={t('createModal.name')}
              type="text"
              placeholder={t('createModal.namePlaceholder')}
              value={newPot.name}
              onChange={(e) => setNewPot((prev) => ({ ...prev, name: e.target.value }))}
            />
            <Labeled label={t('createModal.description')}>
              <textarea
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder={t('createModal.descriptionPlaceholder')}
                value={newPot.description}
                onChange={(e) => setNewPot((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Labeled>
            <Input
              id="target-amount"
              label={t('createModal.targetAmount')}
              type="number"
              placeholder={t('createModal.targetAmountPlaceholder')}
              value={newPot.targetAmount}
              onChange={(e) => setNewPot((prev) => ({ ...prev, targetAmount: e.target.value }))}
            />
            <Labeled label={t('createModal.color')}>
              <div className="grid grid-cols-4 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 ${
                      newPot.color === color.value ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setNewPot((prev) => ({ ...prev, color: color.value }))}
                  />
                ))}
              </div>
            </Labeled>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleCreatePot}>
                {t('create')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>

      {/* Edit Pot Modal */}
      <ModalLayout
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[20px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('editModal.title')}</h2>
          <div className="space-y-4">
            <Input
              id="edit-pot-name"
              label={t('editModal.name')}
              type="text"
              placeholder={t('editModal.namePlaceholder')}
              value={editingPot?.name || ''}
              onChange={(e) =>
                setEditingPot((prev) => (prev ? { ...prev, name: e.target.value } : null))
              }
            />
            <Labeled label={t('editModal.description')}>
              <textarea
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder={t('editModal.descriptionPlaceholder')}
                value={editingPot?.description || ''}
                onChange={(e) =>
                  setEditingPot((prev) => (prev ? { ...prev, description: e.target.value } : null))
                }
              />
            </Labeled>
            <Input
              id="edit-target-amount"
              label={t('editModal.targetAmount')}
              type="number"
              placeholder={t('editModal.targetAmountPlaceholder')}
              value={editingPot?.targetAmount ? (editingPot.targetAmount / 100).toString() : ''}
              onChange={(e) =>
                setEditingPot((prev) =>
                  prev
                    ? {
                        ...prev,
                        targetAmount: e.target.value
                          ? parseInt(e.target.value, 10) * 100
                          : undefined,
                      }
                    : null,
                )
              }
            />
            <Labeled label={t('editModal.color')}>
              <div className="grid grid-cols-4 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 ${
                      editingPot?.color === color.value ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() =>
                      setEditingPot((prev) => (prev ? { ...prev, color: color.value } : null))
                    }
                  />
                ))}
              </div>
            </Labeled>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleEditPot}>
                {t('update')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>

      {/* Break Pot Modal */}
      <ModalLayout
        open={isBreakModalOpen}
        setOpen={setIsBreakModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[20px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold text-secondary">{t('breakModal.title')}</h2>
          <div className="space-y-4">
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-start">
                <TbAlertCircle className="mr-3 mt-0.5 h-5 w-5 text-secondary" />
                <div>
                  <h3 className="text-sm font-medium text-secondary">{t('breakModal.warning')}</h3>
                  <p className="mt-1 text-sm text-secondary">{t('breakModal.description')}</p>
                </div>
              </div>
            </div>

            {breakingPot && (
              <div className="rounded-lg bg-gray-50 p-4">
                <h4 className="mb-2 font-medium text-gray-900">{t('breakModal.potDetails')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('potName')}:</span>
                    <span className="font-medium">{breakingPot.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('currentBalance')}:</span>
                    <span
                      className={`font-medium ${breakingPot.totalBalance < 0 ? 'text-danger' : 'text-success'}`}>
                      {formatEuroCents(breakingPot.totalBalance, i18n.language)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('participants')}:</span>
                    <span className="font-medium">
                      {breakingPot.participants.filter((p) => p.isActive).length}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsBreakModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button
                variant="primary-dark"
                onClick={handleBreakPot}
                className="bg-orange-600 hover:bg-orange-700">
                {t('break')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>
    </>
  );
}
